<template>
  <div class="mt-2 px-6 draft-job-profiles-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />

    <div
      v-if="!libraryLoading"
    >
      <NoDataBlock
        v-if="!jobProfileDrafts.length"
        label="Draft"
        document-type="Job Profiles"
        :show-image="false"
      >
        <template #description>
          There is no Job Profile data available for your selection.
        </template>
      </NoDataBlock>
      <div v-else>
        <StickyListHeader
          v-if="jobProfileDrafts.length"
          class="draft-list-item-wrapper pl-4"
          document-class="item-job-profile-draft"
          :header-columns="headerColumns"
          :selected-sort="selectedSort"
          :sort-direction="sortDirection"
          @submit-sort="submitSort"
        />
        <LibraryList>
          <LibraryListItemWrapper
            v-for="document in draftDocumentsWithItems"
            :key="document.ulid"
            class="draft-list-item-wrapper"
          >
            <router-link
              :to="{ query: { preview: document.ulid, type: 'jobProfile' } }"
            >
              <DraftJobProfilesItem
                :ulid="document.ulid"
                :title="document.title"
                :job-level="document.job_level"
                :job-code="document.job_code"
                :department="document.department"
                :is-archived="document.is_archived"
                :last-revised="document.created_at"
                class="item-job-profile-draft pl-4"
              />
            </router-link>
            <ThreeDotsMenu
              :menu-items="document.items || []"
              :document="document"
              @click="handleMenuItemClick"
            />
          </LibraryListItemWrapper>
        </LibraryList>
      </div>
    </div>

    <StickyPagination
      v-if="totalPages > 1"
      :model-value="page"
      :length="totalPages"
      @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router })"
    />
  </div>
  <DeleteModal
    :show="showDeleteJobModal"
    :name="jobToDelete ? jobToDelete.title : ''"
    document-type="Job Profile"
    detailed-text="job profile"
    @close="showDeleteJobModal = false"
    @delete="handleDeleteDraft"
  />
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useAppLoading } from '@/stores/app/loading';
import { useJobProfileDrafts } from '@/stores/jobProfiles/jobProfileDrafts';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { useAppPagination } from '@/stores/app/pagination';
import { useSubMenu } from '@/stores/filters/subMenu';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import DraftJobProfilesItem from '@/components/jobProfiles/profileListItems/DraftJobProfilesItem.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import ThreeDotsMenu from '@/components/shared/ThreeDotsMenu.vue';
import DeleteModal from '@/components/shared/DeleteModal.vue';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import { stickyHeadersJobProfiles } from '@/helpers/constants/stickyHeaders';
import { EDIT_ICON, DELETE_ICON } from '@/helpers/constants/librariesMenuIcons';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const { jobProfileDrafts, textFilter } = storeToRefs(useJobProfileDrafts());
const { loadJobProfileDrafts, deleteJobProfileByUlid, setTextFilter } = useJobProfileDrafts();
const {
  sendFilterRequest,
  submitSubMenu,
  setFilterConfig,
} = useFilters();
const { showFilterBarSettings } = useAppBase();
const { totalPages, page } = storeToRefs(useAppPagination());
const { selectedSubMenuOption } = storeToRefs(useSubMenu());
const { setSelectedSubMenuOption } = useSubMenu();

const jobToDelete = ref(null);
const showDeleteJobModal = ref(false);

const headerColumns = computed(() => {
  const draftHeaders = [
    'title',
    'jobCode',
    'level',
    'department',
    'status',
    'lastRevised',
  ];

  return draftHeaders.map((header) => stickyHeadersJobProfiles[header]);
});

const selectedSort = computed(() => {
  const fallback = route.query?.sort || 'created_at';
  return (selectedSubMenuOption.value || fallback).replace('-', '');
});

const sortDirection = computed(() => (
  (selectedSubMenuOption.value || route.query?.sort || 'created_at').startsWith('-')
    ? 'desc'
    : 'asc'
));

const getDocumentsWithItems = () => {
  const baseItems = [
    { text: 'Edit', icon: EDIT_ICON, value: 'edit' },
    { text: 'Delete', icon: DELETE_ICON, value: 'delete' },
  ];

  return baseItems;
};

const draftDocumentsWithItems = computed(() => jobProfileDrafts.value.map((profile) => ({
  ...profile, items: getDocumentsWithItems(profile),
})));

const submitSort = (selectedId) => {
  const current = selectedSubMenuOption.value || route.query?.sort || '-created_at';
  const currentBase = current.replace('-', '');

  if (selectedId === '__select') return;

  if (selectedId === currentBase) {
    const optionId = current.startsWith('-') ? selectedId : `-${selectedId}`;

    submitSubMenu({ optionId, router });
  } else {
    submitSubMenu({ optionId: selectedId, router });
  }
};

const handleMenuItemClick = (value, job) => {
  const { ulid } = job;
  const actions = {
    edit: () => {
      router.push({ name: 'job-profile-view', params: { id: ulid } });
    },
    delete: () => {
      jobToDelete.value = job;
      showDeleteJobModal.value = true;
    },
  };

  if (actions[value]) {
    actions[value]();
  }
};

const libraryLoading = computed(() => pageLoading.value || listLoading.value);

const loadPage = async (params) => {
  const searchTerm = textFilter.value || params.searchTerm;
  const mode = params.mode === 'draft-job-profiles' ? 'draft' : params.mode;

  const updatedParams = {
    ...params,
    mode,
    searchTerm,
  };

  await loadJobProfileDrafts(updatedParams);
};

const handleDeleteDraft = async () => {
  if (!jobToDelete?.value?.ulid) return;

  await deleteJobProfileByUlid({ ulid: jobToDelete.value.ulid });
  showDeleteJobModal.value = false;
  jobToDelete.value = null;

  await loadPage({
    routeQuery: route.query,
    router,
    page: route.query?.page || 1,
    mode: 'draft',
    searchTerm: route.query?.q || '',
    selectedMyJobs: route.query?.owned,
  });
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobProfiles',
    showMyJobs: false,
    datepicker: false,
  });

  showFilterBarSettings({ route: route.name });

  if (route.query?.q) setTextFilter(route.query.q);

  await loadPage({
    routeQuery: route.query,
    router,
    page: route.query?.page || 1,
    mode: 'draft',
    searchTerm: route.query?.q || '',
    selectedMyJobs: route.query?.owned,
  });

  emit('track-page-load', { page: 'Job Profile Drafts: Open' });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  setFilterConfig({
    subMenuType: null,
  });
  setSelectedSubMenuOption('');
  setTextFilter('');
});
</script>

<style lang="less" scoped>
.draft-list-item-wrapper {
  display: grid;
  grid-template-columns: 1fr 50px;
}

.item-job-profile-draft, :deep(.item-job-profile-draft) {
  grid-template-columns: minmax(150px, 1fr) minmax(80px, 120px) minmax(150px, 250px) 120px 80px 120px;
}
</style>
